import {
  Timestamp,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

export interface DeliveryRule {
  fee: number;
  maxKm: number;
  minKm: number;
}

export interface DeliveryConfig {
  timeoutMinutes: number;
  maxAttempts: number;
  retryIntervalMinutes: number;
  enableAutoRetry: boolean;
  maxDeliveryRadiusKm?: number;
  weightProximity: number;
  weightRating: number;
  weightAcceptance: number;
  bonusFavorite: number;
  networkLatencyBufferSeconds?: number;
  ttsEnabled?: boolean;
  ttsIntervalSeconds?: number;
  ttsFirstDelaySeconds?: number;
  vibrateEnabled?: boolean;
  vibrateIntervalSeconds?: number;
  enableOfferReminders?: boolean;
  freeKeepAliveMinutes?: number;
  locationRecencyMinutes?: number;
  enableOrderGrouping?: boolean;
  groupingRadiusKm?: number;
  maxOrdersPerBatch?: number;
  groupingTimeWindowMinutes?: number;
  groupingDecisionWindowSeconds?: number;
  groupingMode?: 'radius' | 'route';
  groupingDetourMaxKm?: number;
  groupingDetourMaxRatio?: number;
  groupingCorridorWidthKm?: number;
  groupingMinRouteDistanceKm?: number;
  groupingShortTripRadiusKm?: number;
  groupingOnlyAcceptedByDriver?: boolean;
  groupingRequireNotReady?: boolean;
  delivery_rules?: {
    rules: DeliveryRule[];
  };
  lastUpdated?: Date;
  lastUpdatedBy?: string;
  groupId?: string | null;
}

type DeliveryConfigUpdateOptions = {
  groupId?: string | null;
};

const DEFAULT_CONFIG: DeliveryConfig = {
  timeoutMinutes: 1,
  maxAttempts: 3,
  retryIntervalMinutes: 2,
  enableAutoRetry: true,
  maxDeliveryRadiusKm: 10,
  weightProximity: 0.4,
  weightRating: 0.3,
  weightAcceptance: 0.2,
  bonusFavorite: 100,
  networkLatencyBufferSeconds: 20,
  ttsEnabled: true,
  ttsIntervalSeconds: 8,
  ttsFirstDelaySeconds: 2,
  vibrateEnabled: true,
  vibrateIntervalSeconds: 10,
  enableOfferReminders: false,
  freeKeepAliveMinutes: 3,
  locationRecencyMinutes: 30,
  enableOrderGrouping: false,
  groupingRadiusKm: 1.5,
  maxOrdersPerBatch: 2,
  groupingTimeWindowMinutes: 15,
  groupingDecisionWindowSeconds: 120,
  groupingMode: 'radius',
  groupingDetourMaxKm: 1.2,
  groupingDetourMaxRatio: 0.2,
  groupingCorridorWidthKm: 0.8,
  groupingMinRouteDistanceKm: 1.0,
  groupingShortTripRadiusKm: 1.5,
  groupingOnlyAcceptedByDriver: true,
  groupingRequireNotReady: true,
};

const toJsDate = (value: unknown): Date | undefined => {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return undefined;
};

const scopedRef = (groupId: string) => doc(db, 'delivery_settings', groupId);

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const normalizeNumber = (
  value: unknown,
  fallback: number,
  min?: number,
  max?: number,
  round = false
): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  const bounded =
    min !== undefined && max !== undefined ? clampNumber(parsed, min, max) : parsed;
  return round ? Math.round(bounded) : bounded;
};

const sanitizeRules = (rules: DeliveryRule[] | undefined): DeliveryRule[] | undefined => {
  if (!Array.isArray(rules)) {
    return undefined;
  }

  return rules
    .filter((rule) => rule && typeof rule === 'object')
    .map((rule) => ({
      minKm: normalizeNumber(rule.minKm, 0, 0, 200),
      maxKm: normalizeNumber(rule.maxKm, 0, 0, 200),
      fee: normalizeNumber(rule.fee, 0, 0, 1000),
    }))
    .filter((rule) => rule.maxKm >= rule.minKm);
};

const buildConfig = (
  data: Record<string, any> | undefined,
  groupId?: string | null
): DeliveryConfig => {
  if (!data) {
    return { ...DEFAULT_CONFIG, groupId: groupId || null };
  }

  return {
    ...DEFAULT_CONFIG,
    ...data,
    enableOfferReminders: false,
    groupingMode: data.groupingMode === 'route' ? 'route' : DEFAULT_CONFIG.groupingMode,
    delivery_rules:
      data.delivery_rules && Array.isArray(data.delivery_rules.rules)
        ? { rules: sanitizeRules(data.delivery_rules.rules) || [] }
        : DEFAULT_CONFIG.delivery_rules,
    lastUpdated: toJsDate(data.lastUpdated),
    groupId: groupId || (typeof data.groupId === 'string' ? data.groupId : null),
  };
};

const sanitizeUpdate = (configUpdate: Partial<DeliveryConfig>): Record<string, any> => {
  const sanitized: Record<string, any> = {};

  if (configUpdate.timeoutMinutes !== undefined) {
    sanitized.timeoutMinutes = normalizeNumber(configUpdate.timeoutMinutes, 1, 0.5, 30);
  }
  if (configUpdate.maxAttempts !== undefined) {
    sanitized.maxAttempts = normalizeNumber(configUpdate.maxAttempts, 3, 1, 10, true);
  }
  if (configUpdate.retryIntervalMinutes !== undefined) {
    sanitized.retryIntervalMinutes = normalizeNumber(configUpdate.retryIntervalMinutes, 2, 1, 60);
  }
  if (configUpdate.enableAutoRetry !== undefined) {
    sanitized.enableAutoRetry = !!configUpdate.enableAutoRetry;
  }
  if (configUpdate.maxDeliveryRadiusKm !== undefined) {
    sanitized.maxDeliveryRadiusKm = normalizeNumber(configUpdate.maxDeliveryRadiusKm, 10, 1, 100);
  }

  if (configUpdate.weightProximity !== undefined) {
    sanitized.weightProximity = normalizeNumber(configUpdate.weightProximity, DEFAULT_CONFIG.weightProximity);
  }
  if (configUpdate.weightRating !== undefined) {
    sanitized.weightRating = normalizeNumber(configUpdate.weightRating, DEFAULT_CONFIG.weightRating);
  }
  if (configUpdate.weightAcceptance !== undefined) {
    sanitized.weightAcceptance = normalizeNumber(configUpdate.weightAcceptance, DEFAULT_CONFIG.weightAcceptance);
  }
  if (configUpdate.bonusFavorite !== undefined) {
    sanitized.bonusFavorite = normalizeNumber(configUpdate.bonusFavorite, DEFAULT_CONFIG.bonusFavorite);
  }

  if (configUpdate.networkLatencyBufferSeconds !== undefined) {
    sanitized.networkLatencyBufferSeconds = normalizeNumber(
      configUpdate.networkLatencyBufferSeconds,
      20,
      5,
      30,
      true
    );
  }

  if (configUpdate.ttsEnabled !== undefined) {
    sanitized.ttsEnabled = !!configUpdate.ttsEnabled;
  }
  if (configUpdate.ttsIntervalSeconds !== undefined) {
    sanitized.ttsIntervalSeconds = normalizeNumber(configUpdate.ttsIntervalSeconds, 8, 5, 20, true);
  }
  if (configUpdate.ttsFirstDelaySeconds !== undefined) {
    sanitized.ttsFirstDelaySeconds = normalizeNumber(configUpdate.ttsFirstDelaySeconds, 2, 0, 5, true);
  }
  if (configUpdate.vibrateEnabled !== undefined) {
    sanitized.vibrateEnabled = !!configUpdate.vibrateEnabled;
  }
  if (configUpdate.vibrateIntervalSeconds !== undefined) {
    sanitized.vibrateIntervalSeconds = normalizeNumber(
      configUpdate.vibrateIntervalSeconds,
      10,
      5,
      30,
      true
    );
  }
  sanitized.enableOfferReminders = false;
  if (configUpdate.freeKeepAliveMinutes !== undefined) {
    sanitized.freeKeepAliveMinutes = normalizeNumber(configUpdate.freeKeepAliveMinutes, 3, 1, 30, true);
  }
  if (configUpdate.locationRecencyMinutes !== undefined) {
    sanitized.locationRecencyMinutes = normalizeNumber(
      configUpdate.locationRecencyMinutes,
      30,
      10,
      120,
      true
    );
  }

  if (configUpdate.enableOrderGrouping !== undefined) {
    sanitized.enableOrderGrouping = !!configUpdate.enableOrderGrouping;
  }
  if (configUpdate.groupingRadiusKm !== undefined) {
    sanitized.groupingRadiusKm = normalizeNumber(configUpdate.groupingRadiusKm, 1.5, 0.5, 5);
  }
  if (configUpdate.maxOrdersPerBatch !== undefined) {
    sanitized.maxOrdersPerBatch = normalizeNumber(configUpdate.maxOrdersPerBatch, 2, 2, 5, true);
  }
  if (configUpdate.groupingTimeWindowMinutes !== undefined) {
    sanitized.groupingTimeWindowMinutes = normalizeNumber(
      configUpdate.groupingTimeWindowMinutes,
      15,
      5,
      30,
      true
    );
  }
  if (configUpdate.groupingDecisionWindowSeconds !== undefined) {
    sanitized.groupingDecisionWindowSeconds = normalizeNumber(
      configUpdate.groupingDecisionWindowSeconds,
      120,
      30,
      600,
      true
    );
  }
  if (configUpdate.groupingMode !== undefined) {
    sanitized.groupingMode = configUpdate.groupingMode === 'route' ? 'route' : 'radius';
  }
  if (configUpdate.groupingDetourMaxKm !== undefined) {
    sanitized.groupingDetourMaxKm = normalizeNumber(configUpdate.groupingDetourMaxKm, 1.2, 0.2, 5);
  }
  if (configUpdate.groupingDetourMaxRatio !== undefined) {
    sanitized.groupingDetourMaxRatio = normalizeNumber(configUpdate.groupingDetourMaxRatio, 0.2, 0, 1);
  }
  if (configUpdate.groupingCorridorWidthKm !== undefined) {
    sanitized.groupingCorridorWidthKm = normalizeNumber(
      configUpdate.groupingCorridorWidthKm,
      0.8,
      0.2,
      2
    );
  }
  if (configUpdate.groupingMinRouteDistanceKm !== undefined) {
    sanitized.groupingMinRouteDistanceKm = normalizeNumber(
      configUpdate.groupingMinRouteDistanceKm,
      1.0,
      0.5,
      5
    );
  }
  if (configUpdate.groupingShortTripRadiusKm !== undefined) {
    sanitized.groupingShortTripRadiusKm = normalizeNumber(
      configUpdate.groupingShortTripRadiusKm,
      1.5,
      0.5,
      5
    );
  }
  if (configUpdate.groupingOnlyAcceptedByDriver !== undefined) {
    sanitized.groupingOnlyAcceptedByDriver = !!configUpdate.groupingOnlyAcceptedByDriver;
  }
  if (configUpdate.groupingRequireNotReady !== undefined) {
    sanitized.groupingRequireNotReady = !!configUpdate.groupingRequireNotReady;
  }
  if (configUpdate.delivery_rules?.rules !== undefined) {
    sanitized.delivery_rules = {
      rules: sanitizeRules(configUpdate.delivery_rules.rules) || [],
    };
  }

  return sanitized;
};

export async function getDeliveryConfig(groupId?: string | null): Promise<DeliveryConfig> {
  try {
    if (groupId) {
      const scopedSnap = await getDoc(scopedRef(groupId));
      if (scopedSnap.exists()) {
        return buildConfig(scopedSnap.data() as Record<string, any>, groupId);
      }
    }

    return { ...DEFAULT_CONFIG, groupId: groupId || null };
  } catch (error) {
    console.error('Error fetching delivery config:', error);
    return { ...DEFAULT_CONFIG, groupId: groupId || null };
  }
}

export async function updateDeliveryConfig(
  configUpdate: Partial<DeliveryConfig>,
  options: DeliveryConfigUpdateOptions = {}
): Promise<DeliveryConfig> {
  const groupId = options.groupId || null;
  const sanitized = sanitizeUpdate(configUpdate);

  try {
    if (!groupId) {
      throw new Error('Aucun groupe actif sélectionné pour enregistrer les paramètres livreurs.');
    }

    const batch = writeBatch(db);
    const targetRef = scopedRef(groupId);

    batch.set(
      targetRef,
      {
        ...sanitized,
        groupId,
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );

    await batch.commit();
    return await getDeliveryConfig(groupId);
  } catch (error) {
    console.error('Error updating delivery config:', error);
    throw error;
  }
}
